import React, { useMemo } from 'react';
import { Button, Col, Form, Row } from 'react-bootstrap';
import { hotelsForCity, nightsByCity, resolveHotelForCity, totalPax } from './pricingHelpers';
import dw from './daywiseItinerary.module.css';

const suggestRooms = (travellers, hotel) => {
  const pax = totalPax(travellers);
  if (!hotel?.roomConfigs?.length || pax === 0) {
    return [{ roomType: 'Double', count: 1 }];
  }
  const double =
    hotel.roomConfigs.find((r) => r.roomType === 'Double') || hotel.roomConfigs[0];
  const cap = double.maxTotal || double.maxAdults || 2;
  const count = Math.max(1, Math.ceil(pax / cap));
  return [{ roomType: double.roomType, count }];
};

const BookingSimulator = ({ data, bookingState, onBookingChange, travellers, onTravellersChange }) => {
  const cities = useMemo(() => {
    const nights = nightsByCity(data?.days || []);
    return Object.keys(nights);
  }, [data?.days]);

  const hotelsByCity = useMemo(() => {
    const map = {};
    cities.forEach((city) => {
      map[city] = hotelsForCity(data?.hotels || [], city);
    });
    return map;
  }, [data?.hotels, cities]);

  const setHotel = (city, cityIndex) => {
    if (!onBookingChange) return;
    onBookingChange({
      ...bookingState,
      hotelSelection: { ...(bookingState?.hotelSelection || {}), [city]: String(cityIndex) },
    });
  };

  const setRooms = (city, rooms) => {
    if (!onBookingChange) return;
    onBookingChange({
      ...bookingState,
      roomAllocation: { ...(bookingState?.roomAllocation || {}), [city]: rooms },
    });
  };

  const addRoom = (city) => {
    const current = bookingState?.roomAllocation?.[city] || [{ roomType: 'Double', count: 1 }];
    setRooms(city, [...current, { roomType: 'Double', count: 1 }]);
  };

  const removeRoom = (city, idx) => {
    const current = [...(bookingState?.roomAllocation?.[city] || [])];
    current.splice(idx, 1);
    setRooms(city, current.length ? current : [{ roomType: 'Double', count: 1 }]);
  };

  const autoSuggest = (city) => {
    const sel = bookingState?.hotelSelection?.[city];
    const hotel = resolveHotelForCity(data?.hotels || [], city, sel ?? '0');
    setRooms(city, suggestRooms(travellers, hotel));
  };

  const packageTransports = (data?.transports || []).filter(
    (t) => t.context === 'package' || t.context === 'full_tour'
  );
  const selectedPkg = bookingState?.transportPackageId;

  const validations = [];
  cities.forEach((city) => {
    if (!(hotelsByCity[city]?.length > 0)) {
      validations.push(`No hotels in catalogue for ${city}`);
    }
    const rooms = bookingState?.roomAllocation?.[city] || [];
    const roomCount = rooms.reduce((s, r) => s + (r.count || 0), 0);
    if (roomCount < 1) validations.push(`${city}: assign at least one room`);
  });
  if (totalPax(travellers) === 0) validations.push('Traveller count is zero');

  return (
    <div>
      <div className={dw.sectionHead}>
        <div>
          <h5>1 · Booking party</h5>
          <p>Customer traveller composition for pricing preview.</p>
        </div>
        <span className={dw.tag}>Manual room allocation</span>
      </div>

      <Row className="mb-3">
        {[
          ['indianAdults', 'Indian adults'],
          ['foreignAdults', 'Foreign adults'],
          ['childrenWithBed', 'Children w/ bed'],
          ['childrenNoBed', 'Children no bed'],
        ].map(([key, label]) => (
          <Col md={3} key={key}>
            <label className={dw.fieldLabel}>{label}</label>
            <Form.Control
              type="number"
              min={0}
              value={travellers?.[key] ?? 0}
              onChange={(e) => {
                if (!onTravellersChange) return;
                onTravellersChange({
                  ...travellers,
                  [key]: Math.max(0, parseInt(e.target.value, 10) || 0),
                });
              }}
            />
          </Col>
        ))}
      </Row>

      {cities.map((city) => {
        const options = hotelsByCity[city] || [];
        const rawSel = bookingState?.hotelSelection?.[city] ?? '0';
        const selectedHotel = resolveHotelForCity(data?.hotels || [], city, rawSel);
        const sel =
          options.length > 0
            ? String(Math.max(0, options.indexOf(selectedHotel)))
            : rawSel;
        const rooms = bookingState?.roomAllocation?.[city] || [{ roomType: 'Double', count: 1 }];
        const roomTypes = selectedHotel?.roomConfigs?.map((r) => r.roomType) || ['Double'];

        return (
          <div key={city} className={dw.microcard} style={{ marginBottom: 12 }}>
            <strong>{city}</strong>
            <Form.Group className="mb-2">
              <Form.Label>Hotel</Form.Label>
              <Form.Control
                as="select"
                value={sel}
                onChange={(e) => setHotel(city, e.target.value)}
                disabled={!options.length}
              >
                {options.map((hotel, cityIdx) => (
                  <option key={`${city}-${cityIdx}-${hotel.hotelName || 'hotel'}`} value={String(cityIdx)}>
                    {hotel.hotelName || 'Unnamed'} ({hotel.starRating}★)
                  </option>
                ))}
              </Form.Control>
            </Form.Group>

            <div className="d-flex justify-content-between align-items-center mb-2">
              <strong>Rooms</strong>
              <div>
                <Button size="sm" variant="outline-secondary" className="me-1" onClick={() => autoSuggest(city)}>
                  Auto-suggest
                </Button>
                <Button size="sm" onClick={() => addRoom(city)}>
                  Add room line
                </Button>
              </div>
            </div>

            {rooms.map((line, idx) => (
              <Row key={idx} className="mb-1">
                <Col md={4}>
                  <Form.Control
                    as="select"
                    value={line.roomType}
                    onChange={(e) => {
                      const next = [...rooms];
                      next[idx] = { ...next[idx], roomType: e.target.value };
                      setRooms(city, next);
                    }}
                  >
                    {roomTypes.map((rt) => (
                      <option key={rt} value={rt}>
                        {rt}
                      </option>
                    ))}
                  </Form.Control>
                </Col>
                <Col md={3}>
                  <Form.Control
                    type="number"
                    min={1}
                    value={line.count ?? 1}
                    onChange={(e) => {
                      const next = [...rooms];
                      next[idx] = { ...next[idx], count: Number(e.target.value) };
                      setRooms(city, next);
                    }}
                  />
                </Col>
                <Col md={2}>
                  <Button size="sm" variant="outline-danger" onClick={() => removeRoom(city, idx)}>
                    Remove
                  </Button>
                </Col>
              </Row>
            ))}
          </div>
        );
      })}

      {packageTransports.length > 0 && (
        <Form.Group className="mb-3">
          <Form.Label>Full-tour transport package</Form.Label>
          <Form.Control
            as="select"
            value={selectedPkg || ''}
            onChange={(e) => {
              if (!onBookingChange) return;
              onBookingChange({
                ...bookingState,
                transportPackageId: e.target.value || null,
              });
            }}
          >
            <option value="">— none —</option>
            {packageTransports.map((t) => (
              <option key={t.contextId} value={t.contextId}>
                {t.label || t.mode} · ₹{t.rateShared ?? 0}
              </option>
            ))}
          </Form.Control>
        </Form.Group>
      )}

      {validations.length > 0 ? (
        <div className={dw.panelNoteWarn}>
          <strong>⚠ {validations.length} validation issue(s)</strong>
          <ul style={{ marginBottom: 0, paddingLeft: 18 }}>
            {validations.map((v, i) => (
              <li key={i}>{v}</li>
            ))}
          </ul>
        </div>
      ) : (
        <div className={dw.panelNoteGood}>
          <strong>✓ All basic checks passed.</strong> Traveller counts and room lines look valid.
        </div>
      )}
    </div>
  );
};

export default BookingSimulator;
